import request from '@/api/config.js'
import logger from '@/utils/logger'

/**
 * Получить записи конкретного филиала для календаря
 * Отмененные записи (statusId = 5) исключаются на бэкенде
 * @param {number} departmentId - ID филиала
 * @param {{ from?: string, to?: string, timezone?: string }} [range] - YYYY-MM-DD
 * @returns {Promise} Список записей филиала (id, postId, employeeId, start, end)
 */
export function getRecordsForCalendar(
  departmentId,
  { from, to, timezone } = {}
) {
  if (!from || !to || !timezone) {
    logger.error(
      'records_calendar: from, to и timezone не переданы, запрос не отправляется'
    )
    return Promise.resolve({ data: [] })
  }

  return request({
    url: `record/department/${departmentId}/records_calendar`,
    method: 'get',
    params: {
      from,
      to,
      timezone,
    },
  })
}

/**
 * Записи филиала за один день — для сетки свободных слотов при переносе записи.
 * Границы дня считает бэк в таймзоне филиала. Отменённые записи исключены.
 * @param {number} departmentId - ID филиала
 * @param {{ date: string, timezone?: string }} params - date в формате YYYY-MM-DD (день в таймзоне филиала)
 * @returns {Promise<{ data: Array<{ id: number, postId: number|null, start: string, end: string }> }>}
 */
export function getDepartmentDayRecords(departmentId, { date, timezone } = {}) {
  return request({
    url: `record/department/${departmentId}/day-records`,
    method: 'get',
    params: {
      date,
      ...(timezone ? { timezone } : {}),
    },
  })
}

// Получение списка всех записей(эвентов)
export function getAllRecord() {
  return request({
    url: `record`,
    method: 'get',
  })
}

// Получение информации о записи по ID. В Network отображается как record-detail.
export function getRecordById(id) {
  return request({
    url: `record/${id}/record-detail`,
    method: 'get',
  })
}

// Получение информации о записи(эвенте) по slug
export function getRecordBySlug(slug) {
  return request({
    url: `record/slug/${slug}`,
    method: 'get',
  })
}

export function cancelBySlug(slug, reason) {
  return request({
    url: `record/slug/${slug}/cancel`,
    method: 'post',
    data: { reason },
  })
}

export function requestReschedule(slug) {
  return request({
    url: `record/slug/${slug}/request-reschedule`,
    method: 'post',
  })
}

// Полные записи организации без фильтра по датам. Календарь организации использует getOrgRecordWeek.
// TODO: удалить позже, если после оптимизации календаря нигде не останется в использовании.
export function getOrgRecord(id, options = {}) {
  return request({
    url: `record/org/${id}/org-record`,
    method: 'get',
    params: options.bustCache ? { _rt: Date.now() } : undefined,
    headers: options.bustCache
      ? { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      : undefined,
  })
}

/**
 * Счётчики записей на сегодня по постам организации (страница активных постов).
 * «Сегодня» считается на бэке в таймзоне каждого филиала.
 * @returns {Promise<{ data: Array<{ departmentId: number, postId: number, count: number, nearestStart: string|null }> }>}
 */
export function getPostDayStats(orgId, options = {}) {
  return request({
    url: `record/org/${orgId}/post-day-stats`,
    method: 'get',
    params: options.bustCache ? { _rt: Date.now() } : undefined,
    headers: options.bustCache
      ? { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      : undefined,
  })
}

// Записи организации за период — облегчённый ответ для календаря.
// from, to — YYYY-MM-DD в timezone филиала; timezone — IANA (например Europe/Moscow).
export function getOrgRecordWeek(orgId, from, to, timezone) {
  return request({
    url: `record/org/${orgId}/calendar-records`,
    method: 'get',
    params: {
      from,
      to,
      ...(timezone ? { timezone } : {}),
    },
  })
}

/**
 * Маркеры дней с записями за период (точки в мини-календарях).
 * @returns {Promise<{ data: Array<{ date: string, count: number }> }>}
 */
export function getOrgCalendarRecordMarkers(
  orgId,
  { from, to, timezone, departmentId, postId } = {}
) {
  return request({
    url: `record/org/${orgId}/calendar-record-markers`,
    method: 'get',
    params: {
      from,
      to,
      ...(timezone ? { timezone } : {}),
      ...(departmentId != null ? { departmentId } : {}),
      ...(postId != null ? { postId } : {}),
    },
  })
}

// Получение данных для графика среднего чека по организации
export function getAverageCheckByOrg(id, params = {}) {
  return request({
    url: `record/org/${id}/avg-check`,
    method: 'get',
    params,
  })
}

// Получение списка всех статусов записей(эвентов)
export function getAllStage() {
  return request({
    url: `record/stage`,
    method: 'get',
  })
}

// создание записи(эвента)
export function createRecord(data) {
  return request({
    url: `record/create`,
    method: 'post',
    data,
  })
}

// создание записи(эвента) для клиентского потока с проверкой капчи
export function createClientRecord(data) {
  return request({
    url: `record/client/create`,
    method: 'post',
    data,
  })
}

/** Запись с тем же телефоном, филиалом и временем. */
export function findExistingClientBooking(data) {
  return request({
    url: `record/client/existing`,
    method: 'post',
    data,
  })
}

// редактирование записи(эвента). В Network отображается как "update"
export function updateRecord(id, data) {
  return request({
    url: `record/${id}/update`,
    method: 'put',
    data,
  })
}

// Удаление записи(эвента) по ID
export function deleteRecord(id, { returnVisit = false } = {}) {
  return request({
    url: `record/${id}`,
    method: 'delete',
    params: returnVisit ? { returnVisit: true } : undefined,
  })
}

export function confirmRecord(recordId) {
  return request({
    url: `record/confirm/${recordId}`,
    method: 'post',
  })
}

export function updateRecordStatus() {
  return request({
    url: 'record/update-statuses',
    method: 'post',
  })
}

// Получить ICS файл календаря для записи
export function downloadCalendarFile(recordId) {
  return request({
    url: `record/${recordId}/calendar`,
    method: 'get',
    responseType: 'blob',
  })
}

// Создать ICS файл календаря для записи
export function createCalendarFile(recordId) {
  return request({
    url: `record/${recordId}/calendar`,
    method: 'post',
  })
}
